import { Autocomplete as AutocompletePrimitive } from "@base-ui/react";
import { useQuery } from "@tanstack/react-query";
import {
	Autocomplete,
	AutocompleteContent,
	AutocompleteInput,
	AutocompleteItem,
	AutocompleteList,
} from "@/components/ui/autocomplete";
import { getClientLinkDisplayText, sortClientLinks } from "@/lib/labels";
import type { PhysicalPersonsLookup } from "@/types";

type Person = PhysicalPersonsLookup & { birth_number: string };

interface BirthNumberAutocompleteProps {
	value: string;
	onChange: (value: string) => void;
	onPersonSelected?: (person: Person) => void;
	required?: boolean;
	"aria-invalid"?: boolean;
	"data-warning"?: boolean;
	excludeId?: number;
}

function BirthNumberAutocomplete({
	value,
	onChange,
	onPersonSelected,
	"aria-invalid": ariaInvalid,
	"data-warning": dataWarning,
	required = false,
	excludeId,
}: BirthNumberAutocompleteProps) {
	const { data: persons } = useQuery({
		queryKey: ["persons", { birth_number: value, excludeId }],
		queryFn: async () => {
			const url = new URL("/physical_persons/lookup", window.location.origin);
			url.searchParams.set("birth_number", value);
			if (excludeId) {
				url.searchParams.set("except_id", excludeId.toString());
			}
			const response = await fetch(url.toString());
			if (!response.ok) {
				throw new Error("Failed to fetch persons");
			}
			return response.json() as Promise<Person[]>;
		},
		enabled: value.length >= 3,
	});

	const getStatusText = () => {
		return "Použít existující osobu:";
	};

	return (
		<Autocomplete
			autoHighlight={true}
			items={persons}
			value={value}
			onValueChange={(value, eventDetails) => {
				if (eventDetails.reason === "item-press") {
					return;
				}
				onChange(value);
			}}
			filter={null}
			required={required}
			itemToStringValue={(item) => item.birth_number}
		>
			<AutocompleteInput
				placeholder="Zadejte rodné číslo"
				aria-invalid={ariaInvalid}
				data-warning={dataWarning}
			/>
			<AutocompleteContent
				className={!persons || persons.length === 0 ? "hidden" : ""}
			>
				<AutocompletePrimitive.Status className="px-2 py-1 text-muted-foreground text-sm">
					{getStatusText()}
				</AutocompletePrimitive.Status>
				<AutocompleteList>
					{(item: Person) => {
						const sortedClientLinks = sortClientLinks(item.client_links);
						return (
							<AutocompleteItem
								onClick={() => onPersonSelected?.(item)}
								key={item.birth_number}
								value={item}
							>
								<div className="flex w-full items-center justify-between gap-2">
									<div className="flex flex-col">
										<span className="font-medium text-sm">
											{item.full_name}
										</span>
										<span className="text-muted-foreground text-xs">
											{item.birth_number}
										</span>
									</div>
									<div className="flex items-center gap-1.5">
										<span className="text-muted-foreground text-xs">
											{getClientLinkDisplayText(sortedClientLinks[0])}
											{sortedClientLinks.length > 1 &&
												` +${sortedClientLinks.length - 1}`}
										</span>
									</div>
								</div>
							</AutocompleteItem>
						);
					}}
				</AutocompleteList>
			</AutocompleteContent>
		</Autocomplete>
	);
}

export { BirthNumberAutocomplete };
